// Mirrors of the WideBadge.Shared contracts used by the member portal (camelCase JSON, enums as numbers).

export enum CreditType {
  Mentorship = 1,
  Analyzer = 2,
}

export enum CreditReason {
  Purchase = 1,
  AdminGrant = 2,
  AdminAdjustment = 3,
  AppointmentBooked = 4,
  AppointmentRefund = 5,
  AnalyzerQuestion = 6,
  AnalyzerRefund = 7,
}

export enum PaymentKind {
  Subscription = 1,
  MentorshipPackage = 2,
  AnalyzerCredits = 3,
}

export enum PaymentStatus {
  Paid = 1,
  Refunded = 2,
  Failed = 3,
}

export enum SubscriptionStatus {
  Active = 1,
  PastDue = 2,
  Canceled = 3,
  Expired = 4,
}

export enum SubscriptionSource {
  Manual = 1,
  Stripe = 2,
}

export enum BillingInterval {
  Monthly = 1,
  Yearly = 2,
}

export enum AppointmentStatus {
  Pending = 1,
  Scheduled = 2,
  Confirmed = 3,
  RescheduleRequested = 4,
  CancelRequested = 5,
  ChangeRequested = 6,
  Cancelled = 7,
  Completed = 8,
}

export enum AnalyzerMessageStatus {
  Pending = 1,
  Answered = 2,
  Failed = 3,
}

export interface PagedResult<T> {
  items: T[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

// ---- Auth / account ----
export interface TokenResponse {
  accessToken: string;
  accessTokenExpiresAt: string;
  refreshToken: string;
  refreshTokenExpiresAt: string;
}

export interface RegisterRequest {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string | null;
  password: string;
  confirmPassword: string;
}

export interface ProfileResponse {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string | null;
  preferredCulture: string | null;
  isSuperAdmin: boolean;
  roles: string[];
}

export interface UpdateProfileRequest {
  firstName: string;
  lastName: string;
  phoneNumber: string | null;
  preferredCulture: string | null;
}

export interface ClientConfigResponse {
  turnstileEnabled: boolean;
  turnstileSiteKey: string | null;
  subscriptionsEnabled: boolean;
  onlineSignupEnabled: boolean;
  allowUserRegistration: boolean;
  supportedCultures: string[];
}

// ---- Content ----
export interface MemberBlueprintDto {
  id: string;
  name: string;
  description: string | null;
  fileName: string | null;
  sizeBytes: number | null;
  order: number;
  blueprintCategoryId: string | null;
}

/** A tier on the blueprint page; blueprintCount = active documents in it. */
export interface BlueprintCategoryDto {
  id: string;
  name: string;
  description: string | null;
  order: number;
  blueprintCount: number;
}

export interface BlueprintAccessResponse {
  id: string;
  fileName: string;
  previewUrl: string;
  downloadUrl: string;
  validUntil: string;
}

export interface MemberVideoDto {
  id: string;
  name: string;
  description: string | null;
  playerUrl: string | null;
  thumbnailUrl: string | null;
  durationSeconds: number | null;
  order: number;
}

export interface MemberVideosResponse {
  subscriptionRequired: boolean;
  videos: MemberVideoDto[];
}

export interface DealSourcingFeeDto {
  id: string;
  totalInvestmentCapital: string;
  sourcingFee: string;
  maxFee: string;
  order: number;
  isActive: boolean;
}

// ---- Members ----
export interface CreditPackageDto {
  id: string;
  type: CreditType;
  name: string;
  subtitle: string | null;
  description: string | null;
  features: string[];
  credits: number;
  price: number;
  currency: string;
  isFeatured: boolean;
  order: number;
  isActive: boolean;
}

export interface CreditBalanceDto {
  mentorship: number;
  analyzer: number;
}

export interface CreditTransactionDto {
  id: string;
  type: CreditType;
  amount: number;
  reason: CreditReason;
  referenceId: string | null;
  note: string | null;
  createdAt: string;
}

export interface PaymentDto {
  id: string;
  kind: PaymentKind;
  status: PaymentStatus;
  amount: number;
  currency: string;
  description: string;
  credits: number | null;
  paidAt: string;
}

export interface SubscriptionDto {
  id: string;
  planName: string | null;
  source: SubscriptionSource;
  status: SubscriptionStatus;
  startDate: string;
  endDate: string;
  isActive: boolean;
  isStripe: boolean;
}

export interface MemberSubscriptionDto {
  hasActiveSubscription: boolean;
  status: SubscriptionStatus | null;
  source: SubscriptionSource | null;
  startDate: string | null;
  endDate: string | null;
  canCancel: boolean;
  planName: string | null;
}

export interface MembershipPlanDto {
  id: string;
  name: string;
  description: string | null;
  price: number;
  currency: string;
  billingInterval: BillingInterval;
}

export interface MemberOverviewDto {
  fullName: string;
  email: string;
  subscription: MemberSubscriptionDto;
  credits: CreditBalanceDto;
  upcomingAppointments: AppointmentDto[];
}

export interface CheckoutResponse {
  url: string;
}

export interface AppointmentDto {
  id: string;
  status: AppointmentStatus;
  /** Minutes of mentorship time used (= durationMinutes). */
  credits: number;
  /** Session length: 30, 60 or 90. */
  durationMinutes: number;
  /** Cal.com booking uid once booked. */
  calBookingUid: string | null;
  startTime: string | null;
  endTime: string | null;
  topic: string | null;
  memberNote: string | null;
  adminNote: string | null;
  createdAt: string;
}

export interface AppointmentBookingResponse {
  appointmentId: string;
  /** "calcom" = embed calLink from embedOrigin; "builtin" = pick the time in the portal (Cal.com not configured). */
  mode: string;
  /** "username/event-slug" or "reschedule/{bookingUid}". */
  calLink: string | null;
  embedOrigin: string | null;
  name: string;
  email: string;
  durationMinutes: number;
  /** Environment tag for the booking metadata ("instance"); environments sharing a Cal.com account ignore each other. */
  instance?: string | null;
}

/** One credit = one question to George. */
export interface AnalyzerPricingDto {
  creditPrice: number;
  currency: string;
  minCredits: number;
  maxCredits: number;
  /** False while answers are simulated (PropertyData not configured). */
  live: boolean;
}

export interface MentorshipOptionsDto {
  /** Minutes the member can book, e.g. [30, 60, 90]. */
  sessionLengths: number[];
  mode: string;
  embedOrigin: string;
}

/** Built-in POC scheduler only. */
export interface ConfirmAppointmentRequest {
  startTime: string;
  endTime?: string | null;
}

export interface AnalyzerMessageDto {
  id: string;
  conversationId: string;
  question: string;
  answer: string | null;
  status: AnalyzerMessageStatus;
  creditsUsed: number;
  error: string | null;
  createdAt: string;
  answeredAt: string | null;
}

export interface AskAnalyzerResponse {
  message: AnalyzerMessageDto;
  remainingCredits: number;
  simulated: boolean;
}

export interface AnalyzerConversationDto {
  conversationId: string;
  title: string;
  messageCount: number;
  lastMessageAt: string;
}

export interface ContactRequest {
  name: string;
  email: string;
  subject: string;
  message: string;
}

// ---- Labels ----
export const appointmentStatusLabel: Record<AppointmentStatus, string> = {
  [AppointmentStatus.Pending]: "Pending booking",
  [AppointmentStatus.Scheduled]: "Scheduled",
  [AppointmentStatus.Confirmed]: "Confirmed",
  [AppointmentStatus.RescheduleRequested]: "Reschedule requested",
  [AppointmentStatus.CancelRequested]: "Cancel requested",
  [AppointmentStatus.ChangeRequested]: "Change requested",
  [AppointmentStatus.Cancelled]: "Cancelled",
  [AppointmentStatus.Completed]: "Completed",
};

export const subscriptionStatusLabel: Record<SubscriptionStatus, string> = {
  [SubscriptionStatus.Active]: "Active",
  [SubscriptionStatus.PastDue]: "Past due",
  [SubscriptionStatus.Canceled]: "Cancelled",
  [SubscriptionStatus.Expired]: "Expired",
};

export const paymentKindLabel: Record<PaymentKind, string> = {
  [PaymentKind.Subscription]: "Membership",
  [PaymentKind.MentorshipPackage]: "Mentorship package",
  [PaymentKind.AnalyzerCredits]: "Analyzer credits",
};

export const paymentStatusLabel: Record<PaymentStatus, string> = {
  [PaymentStatus.Paid]: "Paid",
  [PaymentStatus.Refunded]: "Refunded",
  [PaymentStatus.Failed]: "Failed",
};

export const creditReasonLabel: Record<CreditReason, string> = {
  [CreditReason.Purchase]: "Purchase",
  [CreditReason.AdminGrant]: "Granted",
  [CreditReason.AdminAdjustment]: "Adjustment",
  [CreditReason.AppointmentBooked]: "Session booked",
  [CreditReason.AppointmentRefund]: "Session refund",
  [CreditReason.AnalyzerQuestion]: "Analyzer question",
  [CreditReason.AnalyzerRefund]: "Analyzer refund",
};

export const creditTypeLabel: Record<CreditType, string> = {
  [CreditType.Mentorship]: "Mentorship time",
  [CreditType.Analyzer]: "Analyzer credits",
};
